
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Loader2 } from "lucide-react";
import { FraudDetectionState } from "@/hooks/a2a/screening/useFraudDetection";

interface FraudDetectionTabProps {
  fraudDetection: FraudDetectionState;
  isLoading: boolean;
  onStartDetection: () => Promise<void>;
}

export function FraudDetectionTab({
  fraudDetection,
  isLoading,
  onStartDetection
}: FraudDetectionTabProps) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Fraud Detection</h3>
      
      {fraudDetection.status === 'idle' && (
        <Button onClick={onStartDetection} disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <Shield className="mr-2 h-4 w-4" />
              Run Fraud Detection
            </>
          )}
        </Button>
      )}
      
      {fraudDetection.status === 'scanning' && (
        <Badge variant="secondary">Scanning...</Badge>
      )}
      
      {fraudDetection.status === 'completed' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">Fraud Score: {fraudDetection.fraudScore}/100</h3>
              <p className="text-sm text-muted-foreground">
                {fraudDetection.fraudScore ? (
                  fraudDetection.fraudScore < 30 ? 'Low risk' : 
                  fraudDetection.fraudScore < 70 ? 'Moderate risk' : 'High risk'
                ) : 'N/A'}
              </p>
            </div>
            
            <Badge variant={
              fraudDetection.fraudScore ? (
                fraudDetection.fraudScore < 30 ? 'success' : 
                fraudDetection.fraudScore < 70 ? 'warning' : 
                'destructive'
              ) : 'secondary'
            }>
              {fraudDetection.fraudScore ? (
                fraudDetection.fraudScore < 30 ? 'Passed' : 
                fraudDetection.fraudScore < 70 ? 'Review' : 
                'Alert'
              ) : 'Unknown'}
            </Badge>
          </div>
          
          {fraudDetection.alerts && fraudDetection.alerts.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Alerts</h4>
              <ul className="list-disc pl-5">
                {fraudDetection.alerts.map((alert, index) => (
                  <li key={index} className="text-sm">
                    {alert.description} ({alert.severity})
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {fraudDetection.recommendation && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium">Recommendation</h4>
              <p className="text-sm">{fraudDetection.recommendation}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
